import React, { Component } from 'react'
import { List } from 'immutable'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import {
	SortableTreeWithoutDndContext as SortableTree,
	getFlatDataFromTree,
	walk,
} from 'react-sortable-tree'
import FileExplorerTheme from 'react-sortable-tree-theme-file-explorer'
import Mousetrap from 'mousetrap'
import history from 'configureHistory'
import { isMobile } from 'react-device-detect'
import { translate } from 'react-polyglot'
import { makeTree } from 'utils/tree'
import EE from 'emitter.js'

import { Button, Icon, Text, SuggestionModal, Tooltip } from 'components'
import { getShortcutString } from 'components/Shortcut'

import FileThemeTreeNodeRenderer from './SortableTree/NodeRenderer.js'
import FileThemeNodeContentRenderer from './SortableTree/SortableTree.js'

import './sidebar.scss'

// Extend native prototype of the event for browsers like Safari
if (!('path' in Event.prototype)) {
	Object.defineProperty(Event.prototype, 'path', {
		// Don't use shorthand!
		get() {
			const path = []
			let currentElem = this.target

			while (currentElem) {
				path.push(currentElem)
				currentElem = currentElem.parentElement
			}

			if (path.indexOf(window) === -1 && path.indexOf(document) === -1) {
				path.push(document)
			}

			if (path.indexOf(window) === -1) {
				path.push(window)
			}

			return path
		},
	})
}

class Sidebar extends Component {
	constructor(props) {
		super(props)

		this.list = null
		this.dragInterval = null

		const articles = props.articles || new List()

		this.state = {
			treeData: makeTree(articles, props.t),
			searchString: '',
			searchFocusIndex: 0,
			added: false, // Reload menu from props if new entry recently got added
		}
	}

	componentDidMount() {
		Mousetrap(document.querySelector('body')).bind(
			getShortcutString('createEntry'),
			this.onAdd
		)
	}

	componentWillUnmount() {
		Mousetrap(document.querySelector('body')).unbind(
			getShortcutString('createEntry'),
			this.onAdd
		)
	}

	onChange = treeData => {
		try {
			const { articles, updateArticles } = this.props

			// Check if expanded state has changed while dragging
			walk({
				treeData,
				getNodeKey: () => {},
				callback: entry => {
					if (entry.node.expanded) {
						this.setVisibilityState(entry.node.id, true)
					}
				},
				ignoreCollapsed: false,
			})

			this.setState({ treeData })

			// Save changed articles
			const flatTreeData = getFlatDataFromTree({
				treeData,
				getNodeKey: () => {},
			})

			const rootLength = flatTreeData.filter(item => !item.parentNode)
				.length

			const changes = flatTreeData
				.map(item => {
					const { id } = item.node
					const parent = item.parentNode ? item.parentNode.id : null
					const order = item.parentNode
						? item.parentNode.children.length -
						  item.lowerSiblingCounts[
								item.lowerSiblingCounts.length - 1
						  ]
						: rootLength -
						  item.lowerSiblingCounts[
								item.lowerSiblingCounts.length - 1
						  ]

					const article = articles.find(a => a.id === id)

					if (article.order !== order || article.parent !== parent) {
						return { id, order, parent }
					}

					return false
				})
				.filter(item => item)

			if (changes.length) {
				updateArticles(changes)
			}
		} catch (err) {
			this.setState(() => {
				throw err
			})
		}
	}

	setVisibilityState = (id, visible) => {
		localStorage.setItem(`a${id}`, visible)
	}

	confirmAction = text => window.confirm(text)

	onRemove = id => {
		const {
			removeArticle,
			// articles,
			// workspace,
		} = this.props
		// const { treeData } = this.state

		removeArticle(id)

		// If there is only one article left, go there
		// If deleted page was active, jump to first entry in tree
		// or second if deleted one was first one
		// if (articles.size === 1) {
		// 	history.replace('/')
		// } else if (this.props.id === id) {
		// 	// Go to first, else go to second
		// 	if (treeData[0].id !== id) {
		// 		history.replace(`/${workspace}/${treeData[0].id}`)
		// 	} else {
		// 		history.replace(`/${workspace}/${treeData[1].id}`)
		// 	}
		// }
	}

	onAdd = () => {
		const { handleSubmit, articles, id } = this.props

		let currentParent = null
		let lastOrder = 0

		if (articles.size) {
			const current = articles.find(article => article.id === id)

			currentParent = current ? current.parent : null
			lastOrder = articles
				.filter(article => article.parent === currentParent)
				.sort((a, b) => a.order - b.order)
				.last().order
		}

		handleSubmit({ parent: currentParent, order: lastOrder + 1 })

		this.setState({
			added: { parent: currentParent, order: lastOrder + 1 },
		})
	}

	onAddSubEntry = id => {
		const { handleSubmit, articles } = this.props
		const siblings = articles.filter(article => article.parent === id)
		const lastOrder = siblings.size
			? siblings.sort((a, b) => a.order - b.order).last().order
			: 0

		this.setVisibilityState(id, true)
		handleSubmit({ parent: id, order: lastOrder + 1 })

		this.setState({
			added: { parent: id, order: lastOrder + 1 },
		})
	}

	search = (key, searchQuery, node, path, treeIndex) => {
		if (searchQuery.indexOf('LAST') !== -1) {
			searchQuery = searchQuery.replace(searchQuery, 'LAST', '')
		}

		if (searchQuery && searchQuery === node.id) {
			return node
		}
	}

	UNSAFE_componentWillUpdate(nextProps, nextState) {
		if (nextProps.articles !== this.props.articles) {
			if (nextState.added) {
				this.setState({
					treeData: makeTree(nextProps.articles, nextProps.t),
					searchString: nextProps.articles.find(
						article =>
							article.parent === nextState.added.parent &&
							article.order === nextState.added.order
					).id,
					added: false,
				})

				return
			}

			this.setState({
				treeData: makeTree(nextProps.articles, nextProps.t),
			})
		}
	}

	renderTree = () => {
		const {
			onSidebarToggle,
			id,
			isReadOnly,
			workspace,
			userId,
			t,
		} = this.props

		const { treeData, searchString, searchFocusIndex } = this.state

		return (
			<SortableTree
				treeData={treeData}
				onChange={this.onChange}
				searchQuery={searchString}
				searchFocusOffset={searchFocusIndex}
				searchFinishCallback={matches => {
					const { searchString } = this.state

					if (searchString.indexOf('LAST') !== -1) {
						this.setState({
							searchString: '',
							searchFocusIndex:
								matches.length > 0
									? searchFocusIndex % matches.length
									: 0,
						})
					} else if (searchString) {
						this.setState({
							searchString: `${searchString}LAST`,
							searchFocusIndex:
								matches.length > 0
									? searchFocusIndex % matches.length
									: 0,
						})
					}
				}}
				searchMethod={({ node, path, treeIndex, searchQuery }) =>
					this.search('title', searchQuery, node, path, treeIndex)
				}
				placeholderRenderer={() => (
					<Text className="o-sortable-tree__placeholder">
						{t('sidebar.no_entries')}
					</Text>
				)}
				rowHeight={46}
				maxDepth={3}
				className="o-sortable-tree"
				reactVirtualizedListProps={{
					ref: ref => {
						this.list = ref
					},
				}}
				// onDragStateChanged={({ isDragging }) => {
				// 	if (this.list) {
				// 		const recomputeRowHeights = () => {
				// 			console.log('Recompute height …')

				// 			this.list.wrappedInstance.current.recomputeRowHeights()
				// 		}

				// 		if (isDragging) {
				// 			this.dragInterval = setInterval(recomputeRowHeights, 250)
				// 		} else {
				// 			clearInterval(this.dragInterval)

				// 			recomputeRowHeights()
				// 		}
				// 	}
				// }}
				onMoveNode={({ isDragging }) => {
					if (this.list) {
						const recomputeRowHeights = () => {
							// console.log('Recompute height …')

							this.list.wrappedInstance.current.recomputeRowHeights()
						}

						if (isDragging) {
							this.dragInterval = setInterval(
								recomputeRowHeights,
								250
							)
						} else {
							clearInterval(this.dragInterval)

							recomputeRowHeights()
						}
					}
				}}
				// onVisibilityToggle={() => console.log('onVisibilityToggle')}
				theme={FileExplorerTheme}
				treeNodeRenderer={FileThemeTreeNodeRenderer}
				nodeContentRenderer={FileThemeNodeContentRenderer}
				canDrag={!isReadOnly}
				generateNodeProps={rowInfo => {
					const classes = ''

					return {
						className: classes,
						activeId: id,
						isReadOnly,
						userId,
						onRemove: () => this.onRemove(rowInfo.node.id),
						onChangeArticleState: this.props.changeArticleState,
						onChangeOutdatedState: this.props.changeOutdatedState,
						onDuplicate: () =>
							this.props.duplicateArticle(rowInfo.node.id),
						onAddSubEntry: () =>
							this.onAddSubEntry(rowInfo.node.id),
						onClick: e => {
							const parentsFromClick = e.nativeEvent.path
							let classesArr = []

							parentsFromClick.forEach(cur => {
								classesArr =
									typeof cur.className === 'string'
										? [
												...classesArr,
												...cur.className.split(' '),
										  ]
										: classesArr
							})

							if (
								classesArr.indexOf(
									'o-sortable-tree__actions'
								) >= 0
							) {
								return
							}

							if (
								classesArr.indexOf(
									'o-sortable-tree__toggle'
								) === -1
							) {
								history.push(`/${workspace}/${rowInfo.node.id}`)

								if (isMobile) {
									onSidebarToggle()
								}
							}

							// else {
							// 	const rowId = rowInfo.node.id

							// 	if (classesArr.indexOf('o-sortable-tree__toggle--collapse') >= 0) {
							// 		this.setVisibilityState(rowId, false)
							// 	} else {
							// 		this.setVisibilityState(rowId, true)
							// 	}
							// }
						},
					}
				}}
			/>
		)
	}

	renderLogo = () => {
		const {
			theme,
			logo,
			logoRatio,
			logoNegative,
			logoNegativeRatio,
		} = this.props

		if (!logo && !logoNegative) {
			return (
				<img
					className="o-sidebar__logo"
					src="/assets/img/logo-beta.svg"
					alt="Skara"
				/>
			)
		}

		if (theme === 'dark' && logoNegative) {
			return (
				<img
					className={classNames(
						'o-sidebar__logo',
						logoNegativeRatio &&
							logoNegativeRatio < 1 &&
							'o-sidebar__logo--upright'
					)}
					src={`${process.env.STORAGE}/${logoNegative}`}
					alt="Logo"
				/>
			)
		}

		return (
			<img
				className={classNames(
					'o-sidebar__logo',
					logoRatio && logoRatio < 1 && 'o-sidebar__logo--upright'
				)}
				src={`${process.env.STORAGE}/${logo}`}
				alt="Logo"
			/>
		)
	}

	render() {
		const {
			onSidebarToggle,
			isReadOnly,
			logoRatio,
			logoNegativeRatio,
			t,
		} = this.props
		const hasUprightLogo =
			(logoRatio && logoRatio < 1) ||
			(logoNegativeRatio && logoNegativeRatio < 1)

		return (
			<>
				<div
					className={classNames(
						'o-sidebar',
						hasUprightLogo && 'o-sidebar--has-upright-logo'
					)}
				>
					<div className="o-sidebar__header">
						{this.renderLogo()}
						<Button
							icon={<Icon name="sidebar-close" />}
							onClick={onSidebarToggle}
						/>
					</div>
					{isReadOnly ? null : (
						<div className="o-sidebar__call-to-action">
							<Button
								layout="secondary"
								text={t('sidebar.new_entry')}
								icon={<Icon name="new-entry" />}
								onClick={() => {
									this.onAdd()

									if (isMobile) {
										onSidebarToggle()
									}
								}}
							/>
							<Tooltip title={t('give_suggestion')}>
								<Button
									layout="secondary"
									icon={<Icon name="tips" />}
									fullWidth
									onClick={() => {
										EE.emit('showSuggestionModal')
									}}
								/>
							</Tooltip>
						</div>
					)}
					{this.renderTree()}
				</div>
				<SuggestionModal />
			</>
		)
	}
}

Sidebar.propTypes = {
	id: PropTypes.string,
	isReadOnly: PropTypes.bool.isRequired,
	articles: PropTypes.shape(),
	handleSubmit: PropTypes.func.isRequired,
	updateArticles: PropTypes.func.isRequired,
	removeArticle: PropTypes.func.isRequired,
	duplicateArticle: PropTypes.func.isRequired,
	changeArticleState: PropTypes.func.isRequired,
	changeOutdatedState: PropTypes.func.isRequired,
	onSidebarToggle: PropTypes.func.isRequired,
}

Sidebar.defaultProps = {
	id: null,
	articles: null,
}

export default translate()(Sidebar)
